import { lazy, Suspense } from 'react'
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { Toaster } from 'sonner'
import { AuthProvider, useAuth } from '@/hooks/useAuth'
import Brand from '@/components/Brand'

// Route bundles load only when the corresponding page is opened.
const Login = lazy(() => import('@/pages/Login'))
const Register = lazy(() => import('@/pages/Register'))
const ComoFunciona = lazy(() => import('@/pages/ComoFunciona'))
const SetPassword = lazy(() => import('@/pages/SetPassword'))
const Catalog = lazy(() => import('@/pages/Catalog'))
const Cart = lazy(() => import('@/pages/Cart'))
const Checkout = lazy(() => import('@/pages/Checkout'))
const Orders = lazy(() => import('@/pages/Orders'))
const OrderDetail = lazy(() => import('@/pages/OrderDetail'))
const Profile = lazy(() => import('@/pages/Profile'))
const AdminDashboard = lazy(() => import('@/pages/admin/Dashboard'))
const AdminCustomers = lazy(() => import('@/pages/admin/Customers'))
const SellerPortal = lazy(() => import('@/pages/seller/Portal'))
const SellerCustomer = lazy(() => import('@/pages/seller/Customer'))
const SellerQuoteEditor = lazy(() => import('@/pages/seller/QuoteEditor'))
const Samples = lazy(() => import('@/pages/Samples'))
const QuoteReview = lazy(() => import('@/pages/QuoteReview'))
const AdminSalespeople = lazy(() => import('@/pages/admin/Salespeople'))
const AdminCustomerDetail = lazy(() => import('@/pages/admin/CustomerDetail'))
const AdminCustomerEditor = lazy(() => import('@/pages/admin/CustomerEditor'))
const AdminCreateOrder = lazy(() => import('@/pages/admin/CreateOrder'))
const AdminMarketing = lazy(() => import('@/pages/admin/Marketing'))
const AdminOrders = lazy(() => import('@/pages/admin/Orders'))
const AdminPriceLists = lazy(() => import('@/pages/admin/PriceLists'))
const AdminPricing = lazy(() => import('@/pages/admin/Pricing'))
const AdminProspecting = lazy(() => import('@/pages/admin/Prospecting'))
const AdminSettings = lazy(() => import('@/pages/admin/Settings'))
const AdminIntegrations = lazy(() => import('@/pages/admin/Integrations'))
const AdminNotifications = lazy(() => import('@/pages/admin/Notifications'))
const AdminOperations = lazy(() => import('@/pages/admin/Operations'))
const AdminReceivables = lazy(() => import('@/pages/admin/Receivables'))
const AdminCommercialActivity = lazy(() => import('@/pages/admin/CommercialActivity'))
const RepresentativePerformance = lazy(() => import('@/pages/admin/RepresentativePerformance'))
import CustomerActivityMonitor from '@/components/CustomerActivityMonitor'
import ErrorMonitor from '@/components/ErrorMonitor'
import PerformanceMonitor from '@/components/PerformanceMonitor'
import NetworkStatus from '@/components/NetworkStatus'
import PageErrorBoundary from '@/components/PageErrorBoundary'

// Layout
const CustomerLayout = lazy(() => import('@/components/layout/CustomerLayout'))
const AdminLayout = lazy(() => import('@/components/layout/AdminLayout'))
const SellerLayout = lazy(() => import('@/components/layout/SellerLayout'))

// Lojista logado mas sem cadastro aprovado: em analise, recusado, bloqueado ou sem cadastro vinculado.
function CustomerStatusGate({ children }: { children: React.ReactNode }) {
  const { b2bCustomer, isAdmin, signOut, user } = useAuth()

  if (isAdmin || b2bCustomer?.status === 'approved') return <>{children}</>

  const status = b2bCustomer?.status
  const nome = b2bCustomer?.nome_fantasia || b2bCustomer?.razao_social
  const info: Record<string, { title: string; text: string; color: string }> = {
    pending: {
      title: 'Cadastro em analise',
      text: 'Recebemos o cadastro de ' + (nome || 'sua empresa') + '. Nossa equipe esta analisando e voce recebera um e-mail assim que for aprovado.',
      color: 'text-amber-600',
    },
    rejected: {
      title: 'Cadastro nao aprovado',
      text: b2bCustomer?.rejection_reason
        ? 'Motivo informado: ' + b2bCustomer.rejection_reason
        : 'Neste momento nao conseguimos aprovar seu cadastro. Fale com a equipe Maxfem para mais detalhes.',
      color: 'text-red-600',
    },
    blocked: {
      title: 'Acesso bloqueado',
      text: 'Seu acesso ao portal esta bloqueado. Fale com a equipe Maxfem para regularizar.',
      color: 'text-red-600',
    },
  }
  const view = (status && info[status]) || {
    title: 'Cadastro nao encontrado',
    text: 'Nao encontramos um cadastro de lojista para ' + (user?.email || 'este e-mail') + '. Faca seu cadastro para acessar o catalogo.',
    color: 'text-gray-700',
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 px-4">
      <div className="mx-auto w-full max-w-md bg-white rounded-lg shadow-sm p-8 text-center">
        <Brand className="mx-auto w-44 sm:w-44" />
        <h2 className={'mt-6 text-2xl font-bold ' + view.color}>{view.title}</h2>
        <p className="mt-3 text-gray-600">{view.text}</p>
        <p className="mt-1 text-xs text-gray-400">{user?.email}</p>
        <div className="mt-6 flex flex-col gap-2">
          {!status && (
            <a href="/cadastro" className="rounded-lg bg-maxfem-600 px-4 py-2 text-sm font-semibold text-white hover:bg-maxfem-700">
              Fazer cadastro de lojista
            </a>
          )}
          <a href="https://wa.me/5585997491699" target="_blank" rel="noreferrer" className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
            Falar com a Maxfem
          </a>
          <button onClick={() => signOut()} className="text-sm text-gray-500 hover:text-gray-800">
            Sair
          </button>
        </div>
      </div>
    </div>
  )
}

function ProtectedRoute({ children, adminOnly = false, sellerOnly = false }: { children: React.ReactNode; adminOnly?: boolean; sellerOnly?: boolean }) {
  const { user, b2bCustomer, isAdmin, isSalesperson, isLoading, authError, refreshCustomer, signOut } = useAuth()
  const location = useLocation()

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-maxfem-600" />
      </div>
    )
  }

  if (authError) {
    return <div className="min-h-screen flex flex-col items-center justify-center gap-4 p-6 text-center">
      <p role="alert">{authError}</p>
      <button onClick={() => refreshCustomer()} className="rounded-lg bg-maxfem-600 px-4 py-2 text-white">Tentar novamente</button>
      <button onClick={() => signOut()}>Sair</button>
    </div>
  }

  if (!user) {
    const loginParams = new URLSearchParams({ returnTo: location.pathname + location.search })
    if (sellerOnly) loginParams.set('perfil', 'vendedor')
    return <Navigate to={`/login?${loginParams}`} replace />
  }

  if (sellerOnly && !isSalesperson) {
    return <div className="min-h-screen flex flex-col items-center justify-center gap-4 p-6 text-center">
      <Brand className="w-40" />
      <h1 className="text-xl font-semibold">Acesso de vendedor indisponível</h1>
      <p className="max-w-md text-sm text-gray-600">Confirme com a Maxfem se seu cadastro de vendedor está ativo e se este é o e-mail cadastrado.</p>
      <button type="button" onClick={() => refreshCustomer()} className="button-primary">Verificar acesso</button>
      <a href={isAdmin ? '/admin' : '/catalogo'} className="button-secondary">Voltar ao meu painel</a>
      <button type="button" onClick={() => signOut()} className="min-h-11 text-sm">Sair</button>
    </div>
  }
  if (sellerOnly) return <>{children}</>

  if (adminOnly && !isAdmin) {
    return <Navigate to={isSalesperson ? '/vendedor' : '/catalogo'} replace />
  }

  if (!adminOnly) {
    if (!isAdmin && isSalesperson && !b2bCustomer) return <Navigate to="/vendedor" replace />
    return <CustomerStatusGate>{children}</CustomerStatusGate>
  }

  return <>{children}</>
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public */}
      <Route path="/" element={<Navigate to="/login" replace />} />
      <Route path="/login" element={<Login />} />
      <Route path="/cadastro" element={<Register />} />
      <Route path="/como-funciona" element={<ComoFunciona />} />
      <Route path="/definir-senha" element={<SetPassword />} />

      {/* Customer (Lojista) */}
      <Route path="/proposta/:token" element={<ProtectedRoute><CustomerLayout><QuoteReview /></CustomerLayout></ProtectedRoute>} />
      <Route
        path="/catalogo"
        element={
          <ProtectedRoute>
            <CustomerLayout>
              <Catalog />
            </CustomerLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/carrinho"
        element={
          <ProtectedRoute>
            <CustomerLayout>
              <Cart />
            </CustomerLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/checkout"
        element={
          <ProtectedRoute>
            <CustomerLayout>
              <Checkout />
            </CustomerLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/pedidos"
        element={
          <ProtectedRoute>
            <CustomerLayout>
              <Orders />
            </CustomerLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/pedidos/:id"
        element={
          <ProtectedRoute>
            <CustomerLayout>
              <OrderDetail />
            </CustomerLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/perfil"
        element={
          <ProtectedRoute>
            <CustomerLayout>
              <Profile />
            </CustomerLayout>
          </ProtectedRoute>
        }
      />

      <Route path="/vendedor" element={<ProtectedRoute sellerOnly><SellerLayout><SellerPortal /></SellerLayout></ProtectedRoute>} />
      <Route path="/vendedor/amostras" element={<ProtectedRoute sellerOnly><SellerLayout><Samples /></SellerLayout></ProtectedRoute>} />
      <Route path="/vendedor/clientes/:id" element={<ProtectedRoute sellerOnly><SellerLayout><SellerCustomer /></SellerLayout></ProtectedRoute>} />
      <Route path="/vendedor/clientes/:id/proposta" element={<ProtectedRoute sellerOnly><SellerLayout><SellerQuoteEditor /></SellerLayout></ProtectedRoute>} />
      {/* Admin */}
      <Route path="/admin/simulador" element={<ProtectedRoute adminOnly><AdminLayout><AdminPricing /></AdminLayout></ProtectedRoute>} />
      <Route path="/admin/prospeccao" element={<ProtectedRoute adminOnly><AdminLayout><AdminProspecting /></AdminLayout></ProtectedRoute>} />
      <Route
        path="/admin"
        element={
          <ProtectedRoute adminOnly>
            <AdminLayout>
              <Suspense fallback={<div className="py-20 text-center text-sm text-gray-500">Carregando faturamento...</div>}>
                <AdminDashboard />
              </Suspense>
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/vendedores"
        element={
          <ProtectedRoute adminOnly>
            <AdminLayout>
              <AdminSalespeople />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/clientes"
        element={
          <ProtectedRoute adminOnly>
            <AdminLayout>
              <AdminCustomers />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/clientes/:id"
        element={
          <ProtectedRoute adminOnly>
            <AdminLayout>
              <AdminCustomerDetail />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/pedidos"
        element={
          <ProtectedRoute adminOnly>
            <AdminLayout>
              <AdminOrders />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/pedidos/:id"
        element={
          <ProtectedRoute adminOnly>
            <AdminLayout>
              <OrderDetail />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/tabelas"
        element={
          <ProtectedRoute adminOnly>
            <AdminLayout>
              <AdminPriceLists />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/config"
        element={
          <ProtectedRoute adminOnly>
            <AdminLayout>
              <AdminSettings />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route path="/admin/integracoes" element={<ProtectedRoute adminOnly><AdminLayout><AdminIntegrations /></AdminLayout></ProtectedRoute>} />
      <Route path="/admin/clientes/novo" element={<ProtectedRoute adminOnly><AdminLayout><AdminCustomerEditor /></AdminLayout></ProtectedRoute>} />
      <Route path="/admin/clientes/:id/editar" element={<ProtectedRoute adminOnly><AdminLayout><AdminCustomerEditor /></AdminLayout></ProtectedRoute>} />
      <Route path="/admin/pedidos/novo" element={<ProtectedRoute adminOnly><AdminLayout><AdminCreateOrder /></AdminLayout></ProtectedRoute>} />
      <Route path="/admin/marketing" element={<ProtectedRoute adminOnly><AdminLayout><AdminMarketing /></AdminLayout></ProtectedRoute>} />
      <Route path="/admin/banners" element={<Navigate to="/admin/marketing" replace />} />
      <Route path="/admin/pendencias" element={<ProtectedRoute adminOnly><AdminLayout><AdminOperations /></AdminLayout></ProtectedRoute>} />
      <Route path="/admin/recebiveis" element={<ProtectedRoute adminOnly><AdminLayout><AdminReceivables /></AdminLayout></ProtectedRoute>} />
      <Route path="/admin/atividade" element={<ProtectedRoute adminOnly><AdminLayout><AdminCommercialActivity /></AdminLayout></ProtectedRoute>} />
      <Route path="/admin/representantes" element={<ProtectedRoute adminOnly><AdminLayout><RepresentativePerformance /></AdminLayout></ProtectedRoute>} />
      <Route path="/admin/amostras" element={<ProtectedRoute adminOnly><AdminLayout><Samples administrative /></AdminLayout></ProtectedRoute>} />
      <Route path="/admin/notificacoes" element={<ProtectedRoute adminOnly><AdminLayout><AdminNotifications /></AdminLayout></ProtectedRoute>} />
      {/* Fallback */}
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  )
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <NetworkStatus />
        <PerformanceMonitor />
        <CustomerActivityMonitor />
        <ErrorMonitor />
        <PageErrorBoundary>
          <Suspense fallback={<div role="status" className="p-12 text-center text-sm text-gray-500">Carregando…</div>}>
            <AppRoutes />
          </Suspense>
        </PageErrorBoundary>
        {/* O cabeçalho (.workspace-header) tem 73px e é sticky: sem recuo, o
            aviso cobria o carrinho e o perfil. 85px o coloca logo abaixo dele.
            Rodapé não serve: no celular a navegação é fixa embaixo. */}
        <Toaster position="top-right" offset="85px" richColors />
      </AuthProvider>
    </BrowserRouter>
  )
}
